import { sendEmailVerification } from "firebase/auth";
import React, { useState } from "react";
import { createPortal } from "react-dom";
import { useAuth } from "../context/AuthContext";

const EmailVerificationBanner: React.FC = () => {
  const { user } = useAuth();
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState("");
  const [dismissed, setDismissed] = useState(false);

  // Don't show if user is verified, not logged in, or banner is dismissed
  if (!user || user.emailVerified || dismissed) {
    return null;
  }

  const handleResendVerification = async () => {
    if (!user || sending) return;

    setSending(true);
    setMessage("");

    try {
      await sendEmailVerification(user);
      setMessage("Verification email sent! Please check your inbox.");
      setTimeout(() => setMessage(""), 5000);
    } catch (error: any) {
      console.error("Error sending verification email:", error);
      if (error.code === "auth/too-many-requests") {
        setMessage("Too many requests. Please try again later.");
      } else {
        setMessage("Failed to send email. Please try again.");
      }
      setTimeout(() => setMessage(""), 5000);
    } finally {
      setSending(false);
    }
  };

  return createPortal(
    <div className="fixed inset-x-4 bottom-4 z-[60] sm:left-auto sm:right-6 sm:max-w-sm animate-fade-in">
      <div className="bg-gold rounded-lg shadow-[0_12px_30px_-12px_rgba(0,0,0,.8)]">
        <div className="px-4 py-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 flex-1">
            <svg
              className="w-5 h-5 text-night flex-shrink-0 mt-0.5"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                clipRule="evenodd"
              />
            </svg>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-night">
                Verify your email
              </p>
              <p className="text-xs text-night/75 mt-0.5">
                Check your inbox for the verification link
              </p>
              {message && (
                <p className="text-xs text-night/75 mt-1.5 font-medium">{message}</p>
              )}
              <button
                onClick={handleResendVerification}
                disabled={sending}
                className="text-xs font-semibold text-night hover:text-night underline disabled:opacity-50 disabled:cursor-not-allowed mt-2 inline-block"
              >
                {sending ? "Sending…" : "Resend email"}
              </button>
            </div>
          </div>
          <button
            onClick={() => setDismissed(true)}
            className="text-night hover:text-night p-0.5 flex-shrink-0"
            aria-label="Dismiss"
          >
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default EmailVerificationBanner;
